import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';

import { MatIcon } from '@angular/material/icon';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GridItemHTMLElement, GridStack } from 'gridstack';
import { decodeJwt } from 'jose';
import { first, take } from 'rxjs';

import { BreadcrumbComponent } from '../../components/utility/breadcrumb/breadcrumb.component';
import { AccessFacadeService } from '../../services/facades/access-facade.service';
import { getLocalizedWidgets, MULTILINGUAL_WIDGETS } from './dashboard.widgets.config';
import { DashboardFacadeService } from './dashboard-facade.service';

import 'gridstack/dist/gridstack-all.js';

interface GridItem {
  id: string;
  title: string;
  content: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [BreadcrumbComponent, TranslateModule, MatIcon],
})
export class DashboardComponent implements OnInit, AfterViewInit {
  @ViewChild('gridstack') gridstackEl!: ElementRef;

  gridItems: GridItem[] = [];
  userName: string = '';
  grid!: GridStack;
  nextY = 0;

  public currentLanguage: 'en' | 'de' = 'en';

  constructor(
    private accessFacade: AccessFacadeService,
    private translateService: TranslateService,
    private dashboardFacade: DashboardFacadeService,
  ) {
    this.currentLanguage = (this.translateService.currentLang || 'en') as 'en' | 'de';
    this.translateService.onLangChange.subscribe(event => {
      this.currentLanguage = event.lang as 'en' | 'de';
      this.loadWidgetsInCurrentLanguage();
      if (this.grid) {
        this.initializeGridStack();
      }
    });
    this.dashboardFacade.widgetPositions$.subscribe(positions => {
      if (positions.length > 0 && this.grid) {
        this.updateGridItemsWithSavedPositions(positions);
      }
    });
  }

  isTouchDevice: boolean = false;

  ngOnInit(): void {
    this.accessFacade.userTokens$.pipe(first()).subscribe(async t => {
      if (t.token) {
        this.userName = decodeJwt(t.token.access_token)['name'] as string;
      }
    });

    if (this.translateService) {
      this.currentLanguage = (this.translateService.currentLang || 'en') as 'en' | 'de';
    }

    try {
      this.loadWidgetsInCurrentLanguage();

      this.dashboardFacade.validateAndCleanPositions();
    } catch (error) {
      console.error('Error loading widgets, resetting dashboard:', error);
      this.dashboardFacade.resetWidgetPositions();
      this.loadWidgetsInCurrentLanguage();
    }

    this.nextY = 6;
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
  loadWidgetsInCurrentLanguage(): void {
    this.gridItems = getLocalizedWidgets(this.currentLanguage);
  }

  changeLanguage(language: 'en' | 'de'): void {
    this.currentLanguage = language;

    if (this.translateService) {
      this.translateService.use(language);
    }

    this.loadWidgetsInCurrentLanguage();

    if (this.grid) {
      this.initializeGridStack();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initializeGridStack();

      if (this.isTouchDevice) {
        this.setupTouchSupport();
      }
    }, 100);
  }

  setupTouchSupport(): void {
    const gridItems = document.querySelectorAll('.grid-stack-item');

    gridItems.forEach(item => {
      if ((item as any)._touchInitialized) return;
      (item as any)._touchInitialized = true;

      const header = item.querySelector('.dragHandler') as HTMLElement;
      if (!header) return;

      let isDragging = false;

      const touchStart = (e: TouchEvent) => {
        if (e.touches.length !== 1) return;
        e.preventDefault();
        e.stopPropagation();

        isDragging = true;
        item.classList.add('ui-draggable-dragging');

        const touch = e.touches[0];
        const mouseEvent = new MouseEvent('mousedown', {
          clientX: touch.clientX,
          clientY: touch.clientY,
          bubbles: true,
        });
        header.dispatchEvent(mouseEvent);
      };

      const touchMove = (e: TouchEvent) => {
        if (!isDragging || e.touches.length !== 1) return;
        e.preventDefault();
        e.stopPropagation();

        const touch = e.touches[0];
        const mouseEvent = new MouseEvent('mousemove', {
          clientX: touch.clientX,
          clientY: touch.clientY,
          bubbles: true,
        });
        document.dispatchEvent(mouseEvent);
      };

      const touchEnd = (e: TouchEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        e.stopPropagation();

        isDragging = false;
        item.classList.remove('ui-draggable-dragging');

        const mouseEvent = new MouseEvent('mouseup', { bubbles: true });
        document.dispatchEvent(mouseEvent);

        setTimeout(() => this.updateGridItemPositions(), 50);
      };

      header.addEventListener('touchstart', touchStart, { passive: false });
      header.addEventListener('touchmove', touchMove, { passive: false });
      header.addEventListener('touchend', touchEnd, { passive: false });
    });
  }

  initializeGridStack(): void {
    const options = {
      cellHeight: 100,
      margin: 10,
      float: true,
      draggable: {
        handle: '.dragHandler',
        scroll: false,
        appendTo: 'parent',
        tolerance: 'pointer',
        distance: 3,
      },
      resizable: {
        handles: 'se',
      },
      alwaysShowResizeHandle: true,
      disableOneColumnMode: true,
      staticGrid: false,
      column: 6,
    } as any;

    this.grid = GridStack.init(options, this.gridstackEl.nativeElement);

    this.grid.removeAll();

    this.dashboardFacade.widgetPositions$.pipe(take(1)).subscribe(savedPositions => {
      const itemsToRender: any = savedPositions.length > 0 ? savedPositions : this.gridItems;

      itemsToRender.forEach((item: any) => {
        const gridItem = this.gridItems.find(gi => gi.id === item.id) || item;

        const content = `
        <div class="grid-stack-item-content" style="background-color: white; border: 1px solid #d1d5db; box-shadow: 0 2px 5px rgba(0,0,0,0.1); height: 100%; display: flex; flex-direction: column; overflow: auto;">
          <div class="item-header dragHandler" style="background-color:rgb(152, 154, 158); color: white; display: flex; justify-content: space-between; padding: 6px; position: sticky; top: 0; z-index: 2;">
            <span class="item-title" style="font-size: 0.75rem; font-weight: 500;">${gridItem.title}</span>
            <button class="item-button" style="font-size: 0.7rem;" onclick="document.dispatchEvent(new CustomEvent('removeBox', {detail: '${gridItem.id}'}))">✕</button>
          </div>
          <div class="item-body" style="padding: 12px; background-color: white; flex: 1; font-size: 0.75rem;">
            ${gridItem.content.replace(
              /font-size: ([\d.]+)(rem|px);/g,
              function (match: any, size: any, unit: any) {
                let newSize;
                if (unit === 'rem') {
                  newSize = parseFloat(size) * 0.85;
                } else {
                  newSize = Math.round(parseFloat(size) * 0.85);
                }
                return `font-size: ${newSize}${unit};`;
              },
            )}
          </div>
        </div>
      `;

        this.grid.addWidget({
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          id: item.id,
          content: content,
        });
      });

      this.grid.on('change', (event: Event) => {
        this.updateGridItemPositions();
      });

      this.grid.on('change dragstop', (event: Event) => {
        this.updateGridItemPositions();
      });

      // this.grid.on('dropped', (event: Event) => {
      //   this.updateGridItemPositions();
      // });

      document.addEventListener('removeBox', (e: any) => {
        this.removeItem(e.detail);
      });
    });
  }

  addItem(): void {
    if (!this.grid) {
      console.error('Grid not initialized yet');
      return;
    }

    if (this.gridItems.length >= 50) {
      alert(
        this.currentLanguage === 'en'
          ? 'Maximum number of widgets reached. Please remove some widgets before adding more.'
          : 'Maximale Anzahl an Widgets erreicht. Bitte entfernen Sie einige Widgets, bevor Sie weitere hinzufügen.',
      );
      return;
    }

    const nextPosition = this.calculateNextPosition();

    const itemId = `box-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    const templates = MULTILINGUAL_WIDGETS;
    const template = templates[Math.floor(Math.random() * templates.length)];

    const templateTitle = template.translations[this.currentLanguage].title;
    let widgetContent = template.translations[this.currentLanguage].content;

    const isIframeWidget =
      template.id === 'iframe-preview-template' || template.id === 'example-website';
    const isExampleWidget = template.id === 'example-website';

    const iframeId = isIframeWidget ? `iframe-${Date.now()}` : '';

    if (isIframeWidget && !isExampleWidget) {
      widgetContent = widgetContent.replace(
        '<iframe src="about:blank"',
        `<iframe id="${iframeId}" src="about:blank"`,
      );

      widgetContent = widgetContent.replace(
        '<div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0;',
        `<div class="iframe-overlay" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0;`,
      );
    } else if (isExampleWidget) {
      const urlPattern = this.currentLanguage === 'en' ? 'example.com' : 'beispiel.de';

      widgetContent = widgetContent.replace(
        `<iframe src="https://${urlPattern}"`,
        `<iframe id="${iframeId}" src="https://${urlPattern}"`,
      );

      widgetContent = widgetContent.replace(
        'sandbox="allow-same-origin"',
        'sandbox="allow-same-origin allow-scripts"',
      );
    }

    const newItem: GridItem = {
      id: itemId,
      title: templateTitle,
      content: widgetContent,
      x: nextPosition.x,
      y: nextPosition.y,
      w: template.w || 2,
      h: template.h || 2,
    };

    this.gridItems.push(newItem);

    const content = `
      <div class="grid-stack-item-content" style="background-color: white; border: 1px solid #d1d5db; box-shadow: 0 2px 5px rgba(0,0,0,0.1); height: 100%; display: flex; flex-direction: column; overflow: auto;">
        <div class="item-header dragHandler" style="background-color: #6b7280; color: white; display: flex; justify-content: space-between; padding: 8px; position: sticky; top: 0; z-index: 2;">
          <span class="item-title" style="font-size: 0.75rem; font-weight: 500;">${newItem.title}</span>
          <button class="item-button" style="font-size: 0.7rem;" onclick="document.dispatchEvent(new CustomEvent('removeBox', {detail: '${newItem.id}'}))">✕</button>
        </div>
        <div class="item-body" style="padding: 12px; background-color: white; flex: 1; font-size: 0.75rem;">
          ${newItem.content.replace(/font-size: ([\d.]+)(rem|px);/g, function (match, size, unit) {
            let newSize;
            if (unit === 'rem') {
              newSize = parseFloat(size) * 0.85;
            } else {
              newSize = Math.round(parseFloat(size) * 0.85);
            }
            return `font-size: ${newSize}${unit};`;
          })}
        </div>
      </div>
    `;

    this.grid.addWidget({
      x: newItem.x,
      y: newItem.y,
      w: newItem.w,
      h: newItem.h,
      id: newItem.id,
      content: content,
    });

    if (isIframeWidget) {
      setTimeout(() => {
        this.setupIframeWidgetEvents(newItem.id, iframeId, isExampleWidget);
      }, 100);
    }

    setTimeout(() => {
      this.dashboardFacade.validateAndCleanPositions();
    }, 200);

    if (this.isTouchDevice) {
      setTimeout(() => {
        this.setupTouchSupport();
      }, 200);
    }
  }

  setupIframeWidgetEvents(
    widgetId: string,
    iframeId: string,
    isExampleWidget: boolean = false,
  ): void {
    const widgetElement = document.querySelector(`.grid-stack-item[gs-id="${widgetId}"]`);
    if (!widgetElement) return;

    const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
    if (!iframe) return;

    const promptMsg =
      this.currentLanguage === 'en' ? 'Enter the URL to load:' : 'URL zum Laden eingeben:';
    const promptNewUrlMsg =
      this.currentLanguage === 'en' ? 'Enter a new URL:' : 'Neue URL eingeben:';
    const configUrlFirstMsg =
      this.currentLanguage === 'en'
        ? 'Please configure a URL first'
        : 'Bitte zuerst eine URL konfigurieren';

    if (!isExampleWidget) {
      const configButton = widgetElement.querySelector('button[style*="margin-top: 16px"]');
      const overlay = widgetElement.querySelector('.iframe-overlay') as HTMLElement;

      if (configButton && overlay) {
        configButton.addEventListener('click', () => {
          const url = prompt(promptMsg, 'https://example.com');
          if (url) {
            overlay.style.display = 'none';
            iframe.src = url;
          }
        });
      }
    }

    const changeUrlButton = widgetElement.querySelector(
      'button[style*="color: #434ce8; border: 1px solid"]',
    );
    if (changeUrlButton) {
      changeUrlButton.addEventListener('click', () => {
        const defaultUrl =
          this.currentLanguage === 'en' ? 'https://example.com' : 'https://beispiel.de';
        const currentUrl = iframe.src || defaultUrl;
        const newUrl = prompt(promptNewUrlMsg, currentUrl);
        if (newUrl) {
          iframe.src = newUrl;
          const overlay = widgetElement.querySelector('.iframe-overlay') as HTMLElement;
          if (overlay) overlay.style.display = 'none';
        }
      });
    }

    const openTabButton = widgetElement.querySelector(
      'button[style*="background-color: #434ce8; color: white"]',
    );
    if (openTabButton) {
      openTabButton.addEventListener('click', () => {
        const url = iframe.src;
        if (url && url !== 'about:blank') {
          window.open(url, '_blank');
        } else {
          alert(configUrlFirstMsg);
        }
      });
    }
  }
  removeItem(id: string): void {
    const index = this.gridItems.findIndex(item => item.id === id);
    if (index !== -1) {
      this.gridItems.splice(index, 1);

      this.dashboardFacade.removeWidgetPosition(id);

      if (this.grid) {
        const el = document.querySelector(`.grid-stack-item[gs-id="${id}"]`);
        if (el) {
          this.grid.removeWidget(el as HTMLElement);
        } else {
          console.error(`Element with gs-id ${id} not found in DOM`);
        }
      }
    } else {
      console.error(`Item with ID ${id} not found in data model`);
    }
  }

  calculateNextPosition(): { x: number; y: number } {
    const items = this.grid.getGridItems();

    if (items.length === 0) {
      return { x: 0, y: 0 };
    }

    let maxY = 0;

    items.forEach((item: GridItemHTMLElement) => {
      const y = parseInt(item.getAttribute('gs-y') || '0', 10);
      const h = parseInt(item.getAttribute('gs-h') || '1', 10);
      const bottomY = y + h;

      if (bottomY > maxY) {
        maxY = bottomY;
      }
    });

    const x = (items.length % 3) * 2;

    return { x, y: maxY };
  }

  updateGridItemsWithSavedPositions(positions: any[]): void {
    if (!this.grid) return;

    positions.forEach(position => {
      const existingWidget = this.grid
        .getGridItems()
        .find((el: GridItemHTMLElement) => el.getAttribute('gs-id') === position.id);

      if (existingWidget) {
        this.grid.update(existingWidget, {
          x: position.x,
          y: position.y,
          w: position.w,
          h: position.h,
        });
      }
    });
  }

  updateGridItemPositions(): void {
    setTimeout(() => {
      const items = this.grid.getGridItems();
      const updatedPositions = items.map((item: GridItemHTMLElement) => {
        const id = item.getAttribute('gs-id');
        return {
          id: id || '',
          x: parseInt(item.getAttribute('gs-x') || '0', 10),
          y: parseInt(item.getAttribute('gs-y') || '0', 10),
          w: parseInt(item.getAttribute('gs-w') || '1', 10),
          h: parseInt(item.getAttribute('gs-h') || '1', 10),
        };
      });

      updatedPositions.forEach(position => {
        this.dashboardFacade.updateWidgetPosition(position);
      });
    }, 100);
  }

  resetDashboard(): void {
    if (
      confirm(
        this.currentLanguage === 'en'
          ? 'This will reset all widget positions. Continue?'
          : 'Dies wird alle Widget-Positionen zurücksetzen. Fortfahren?',
      )
    ) {
      this.dashboardFacade.resetWidgetPositions();
      this.gridItems = [];

      if (this.grid) {
        this.grid.removeAll();
      }

      this.loadWidgetsInCurrentLanguage();
      this.initializeGridStack();
    }
  }
}
